export const EFFECTS = ["normal", "deep", "high", "robot", "cathedral", "radio"];
const OUT_RATE = 22050;

async function decodeMono(blob) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
  ctx.close();
  const off = new OfflineAudioContext(1, Math.ceil(buf.duration * OUT_RATE), OUT_RATE);
  const src = off.createBufferSource();
  src.buffer = buf;
  src.connect(off.destination);
  src.start();
  return (await off.startRendering()).getChannelData(0);
}

// Granular overlap-add pitch shift that keeps duration
function pitchShift(input, ratio) {
  const grain = 1024, hop = grain / 4;
  const out = new Float32Array(input.length);
  const norm = new Float32Array(input.length);
  const win = Float32Array.from({ length: grain }, (_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (grain - 1)));
  for (let o = 0; o < input.length; o += hop) {
    for (let i = 0; i < grain && o + i < input.length; i++) {
      const pos = Math.max(0, o + grain / 2 + (i - grain / 2) * ratio);
      const j = Math.floor(pos), f = pos - j;
      const s = (input[j] || 0) * (1 - f) + (input[j + 1] || 0) * f;
      out[o + i] += s * win[i];
      norm[o + i] += win[i];
    }
  }
  for (let i = 0; i < out.length; i++) if (norm[i] > 1e-3) out[i] /= norm[i];
  return out;
}

function robot(input) {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const mod = Math.sin((2 * Math.PI * 55 * i) / OUT_RATE);
    out[i] = Math.tanh((input[i] * mod * 0.85 + input[i] * 0.15) * 2.2) * 0.8;
  }
  return pitchShift(out, 0.92);
}

async function renderGraph(input, extraSeconds, build) {
  const off = new OfflineAudioContext(1, input.length + Math.ceil(extraSeconds * OUT_RATE), OUT_RATE);
  const buf = off.createBuffer(1, input.length, OUT_RATE);
  buf.copyToChannel(input, 0);
  const src = off.createBufferSource();
  src.buffer = buf;
  build(off, src);
  src.start();
  return (await off.startRendering()).getChannelData(0);
}

function cathedral(input) {
  return renderGraph(input, 3, (ctx, src) => {
    const len = 3.2 * OUT_RATE;
    const ir = ctx.createBuffer(1, len, OUT_RATE);
    const d = ir.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
    const conv = ctx.createConvolver();
    conv.buffer = ir;
    const dry = ctx.createGain(); dry.gain.value = 0.65;
    const wet = ctx.createGain(); wet.gain.value = 0.35;
    src.connect(dry).connect(ctx.destination);
    src.connect(conv).connect(wet).connect(ctx.destination);
  });
}

async function radio(input) {
  const out = await renderGraph(input, 0, (ctx, src) => {
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 600;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 2800;
    const peak = ctx.createBiquadFilter(); peak.type = "peaking"; peak.frequency.value = 1500; peak.gain.value = 8;
    const shaper = ctx.createWaveShaper();
    shaper.curve = Float32Array.from({ length: 1024 }, (_, i) => Math.tanh(((i / 512) - 1) * 3));
    src.connect(hp).connect(lp).connect(peak).connect(shaper).connect(ctx.destination);
  });
  for (let i = 0; i < out.length; i++) out[i] = out[i] * 0.8 + (Math.random() * 2 - 1) * 0.012;
  return out;
}

function encodeWav(samples) {
  const view = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const str = (o, s) => [...s].forEach((c, i) => view.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF"); view.setUint32(4, 36 + samples.length * 2, true); str(8, "WAVE"); str(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, OUT_RATE, true); view.setUint32(28, OUT_RATE * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); str(36, "data");
  view.setUint32(40, samples.length * 2, true);
  let peak = 0;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]));
  const gain = peak > 0.98 ? 0.98 / peak : 1;
  for (let i = 0; i < samples.length; i++) view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, samples[i] * gain)) * 0x7fff, true);
  return new Blob([view], { type: "audio/wav" });
}

export async function applyEffect(blob, effect) {
  if (effect === "normal") return blob;
  const input = await decodeMono(blob);
  const fx = {
    deep: () => pitchShift(input, 0.75),
    high: () => pitchShift(input, 1.4),
    robot: () => robot(input),
    cathedral: () => cathedral(input),
    radio: () => radio(input),
  }[effect];
  return encodeWav(await fx());
}
